import { Injectable } from '@angular/core';
import * as GridLabelActions from '@app/pcs/actions/grid-label';
import { GridLabelActionTypes } from '@app/pcs/actions/grid-label';
import * as LabelActions from '@app/pcs/actions/label';
import { DossierSearchItem, GridType, UserLabel } from '@app/pcs/models';
import { GridService } from '@app/pcs/services/grid.service';
import { UserLabelDataService } from '@app/pcs/services/user-label-data.service';
import { toDossierIds, updateGridDataForSelection, updateGridElements } from '@app/pcs/util';
import { getLabelId, labelIdEq } from '@app/pcs/util/user-label';
import * as fromRoot from '@app/reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { hasValue, mapPayload } from '@util';
import * as R from 'ramda';
import { of } from 'rxjs';
import { catchError, exhaustMap, switchMap, withLatestFrom } from 'rxjs/operators';

const userLabelIdEq = (userLabel) => labelIdEq(getLabelId(userLabel));
type LabelGridAction = (userLabel: UserLabel) => (gridElement: DossierSearchItem) => any;
const addLabelToGrid: LabelGridAction = R.curry((userLabel: UserLabel, gridElement: DossierSearchItem) => {
  const existingLabel = R.find(userLabelIdEq(userLabel))(gridElement.labels || []);
  return R.evolve({
    labels: hasValue(existingLabel) ? R.identity : R.append(userLabel)
  }, gridElement);
});

const removeLabelFromGrid: LabelGridAction = R.curry((userLabel: UserLabel, gridElement: DossierSearchItem) => {
  const index: number = R.findIndex(userLabelIdEq(userLabel))(gridElement.labels);
  return R.evolve({
    labels: R.remove(index, 1)
  }, gridElement);
});

const shouldUpdateGridLabel = R.curry((userLabel: UserLabel, gridElement: DossierSearchItem) => {
  const existingLabel = R.find(userLabelIdEq(userLabel))(gridElement.labels || []);
  return hasValue(existingLabel);
});

const updateGridLabel = R.curry((userLabel: UserLabel, gridElement: DossierSearchItem) => {
  const userLabels = gridElement.labels;
  const {label, color} = userLabel;
  const index: number = R.findIndex(userLabelIdEq(userLabel))(userLabels);
  const updatedUserLabel = {
    ...userLabels[index],
    label,
    color
  };

  return R.evolve({
    labels: R.update(index, updatedUserLabel)
  }, gridElement);
});

type PrepareLabel = (userLabel: UserLabel) => UserLabel;
const prepareLabel: PrepareLabel = ({label, color}: UserLabel) => ({label: R.pick(['id', 'name'], label), color});

@Injectable()
export class GridLabelEffects {
  gridState$ = this.store.pipe(select(fromRoot.getGridState));

  selectedDossierIds$ = this.gridService.getSelectedIds(GridType.Search, toDossierIds);

  addLabelToSelectedDossiers$ = createEffect(() => this.actions$.pipe(
    ofType<GridLabelActions.AddLabelToSelectedDossiers>(GridLabelActionTypes.AddLabelToSelectedDossiers),
    mapPayload(),
    withLatestFrom(this.selectedDossierIds$),
    exhaustMap(([userLabel, dossierIds]: [UserLabel, number[]]) =>
      this.userLabelDataService.addDossierLabel(getLabelId(userLabel), dossierIds)
        .pipe(
          switchMap((label: UserLabel) => of(updateGridDataForSelection(dossierIds, addLabelToGrid(prepareLabel(label))))),
          catchError((error: Error) => of(new GridLabelActions.AddLabelToSelectedDossiersFail(error)))
        )
    )
  ));

  removeLabelFromSelectedDossiers$ = createEffect(() => this.actions$.pipe(
    ofType<GridLabelActions.RemoveLabelFromSelectedDossiers>(GridLabelActionTypes.RemoveLabelFromSelectedDossiers),
    mapPayload(),
    withLatestFrom(this.selectedDossierIds$),
    exhaustMap(([userLabel, dossierIds]: [UserLabel, number[]]) =>
      this.userLabelDataService.removeDossierLabel(getLabelId(userLabel), dossierIds)
        .pipe(
          switchMap((label: UserLabel) => of(updateGridDataForSelection(dossierIds, removeLabelFromGrid(prepareLabel(label))))),
          catchError((error: Error) => of(new GridLabelActions.RemoveLabelFromSelectedDossiersFail(error)))
        )
    )
  ));

  createDossierLabel$ = createEffect(() => this.actions$.pipe(
    ofType<GridLabelActions.CreateDossierLabel>(GridLabelActionTypes.CreateDossierLabel),
    mapPayload(),
    withLatestFrom(this.selectedDossierIds$),
    exhaustMap(([labelName, dossierIds]: [string, number[]]) =>
      this.userLabelDataService.createDossierLabel(labelName, dossierIds)
        .pipe(
          switchMap((label: UserLabel) => [
            new LabelActions.CreateDossierLabelSuccess(label),
            updateGridDataForSelection(dossierIds, addLabelToGrid(prepareLabel(label)))
          ]),
          catchError((error: Error) => of(new GridLabelActions.CreateDossierLabelFail(error)))
        )
    )
  ));

  updateLabel$ = createEffect(() => this.actions$.pipe(
    ofType<GridLabelActions.UpdateLabel>(GridLabelActionTypes.UpdateLabel),
    mapPayload(),
    switchMap((label: UserLabel) => of(updateGridElements(shouldUpdateGridLabel(label), updateGridLabel(label)))),
    catchError((error: Error) => of(new GridLabelActions.UpdateLabelFail(error)))
  ));

  constructor(private actions$: Actions,
    private store: Store<any>,
    private userLabelDataService: UserLabelDataService,
    private gridService: GridService) {
  }

}
